import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component - Handles meta tags, Open Graph, Twitter Cards, and Schema markup
 * 
 * Usage:
 * <SEO 
 *   title="Page Title"
 *   description="Page description"
 *   keywords="keyword1, keyword2"
 *   canonical="https://optionsanalogies.com/page"
 *   type="article" // or "website"
 *   schema={schemaObject}
 * />
 */

const SEO = ({
  title = 'Options Analogies - Learn Options Trading Through Clear Analogies',
  description = 'Master options trading with plain-English analogies. Learn cash-secured puts, covered calls, and the wheel strategy. Free educational resources by Datatomic Labs.',
  keywords = 'options trading, learn options, cash secured puts, covered calls, wheel strategy, options education, options for beginners',
  canonical,
  type = 'website',
  image = 'https://optionsanalogies.com/og-image.png',
  schema,
  article = false,
  publishedTime,
  modifiedTime,
  author = 'Datatomic Labs'
}) => {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://optionsanalogies.com';
  const fullTitle = title.includes('Options Analogies') ? title : `${title} | Options Analogies`;
  const canonicalUrl = canonical || `${siteUrl}${typeof window !== 'undefined' ? window.location.pathname : '/'}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Options Analogies" />
      <meta property="og:locale" content="en_US" />

      {/* Article Specific */}
      {article && (
        <>
          <meta property="article:author" content={author} />
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          <meta property="article:section" content="Options Trading Education" />
        </>
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Additional SEO */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />

      {/* Schema.org JSON-LD */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

// Schema Generators
export const generateOrganizationSchema = () => {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://optionsanalogies.com';
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Options Analogies",
    "alternateName": "Datatomic Labs Options Education",
    "description": "Educational platform teaching options trading through clear, memorable analogies",
    "url": siteUrl,
    "logo": `${siteUrl}/logo.png`,
    "foundingDate": "2025",
    "founder": {
      "@type": "Organization",
      "name": "Datatomic Labs"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "contact@datatomiclabs.com",
      "contactType": "Customer Service",
      "availableLanguage": "English"
    },
    "sameAs": [
      "https://twitter.com/optionsanalogies",
      "https://github.com/datatomiclabs"
    ]
  };
};

export const generateArticleSchema = ({
  title,
  description,
  url,
  datePublished,
  dateModified,
  author = 'Datatomic Labs',
  image
}) => {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://optionsanalogies.com';
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "url": url,
    "datePublished": datePublished,
    "dateModified": dateModified || datePublished,
    "author": {
      "@type": "Organization",
      "name": author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Options Analogies",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/logo.png`
      }
    },
    "image": image || `${siteUrl}/og-image.png`,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    }
  };
};

export const generateBreadcrumbSchema = (items) => {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://optionsanalogies.com';
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${siteUrl}${item.path}`
    }))
  };
};

export const generateFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const generateHowToSchema = ({
  name,
  description,
  steps,
  totalTime
}) => {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://optionsanalogies.com';
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": name,
    "description": description,
    "totalTime": totalTime,
    "step": steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.description,
      "url": `${siteUrl}${step.url}`
    }))
  };
};

export const generateVideoSchema = ({
  name,
  description,
  uploadDate,
  thumbnailUrl,
  contentUrl
}) => {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://optionsanalogies.com';
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": name,
    "description": description,
    "thumbnailUrl": thumbnailUrl,
    "uploadDate": uploadDate,
    "contentUrl": contentUrl,
    "embedUrl": contentUrl.replace('watch?v=', 'embed/'),
    "duration": "PT10M", // Placeholder - update with actual video duration if known
    "publisher": {
      "@type": "Organization",
      "name": "Options Analogies",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/logo.png`
      }
    }
  };
};

export default SEO;
